import React, { useRef } from 'react';
import { differenceInYears } from 'date-fns';
import { setAdultsNum, setChildrenNum, setChildAge } from '../../actions/requestFormActions';
import { AdultsIcon } from '../Icons/AdultsIcon';
import { ChildrenIcon } from '../Icons/ChildrenIcon';
import { CalendarIcon } from '../Icons/CalendarIcon';
import { __ } from '../../helpers/TranslationService';
import { IAge } from '../../interfaces/IRequestForm';
import { isDateValid } from '../../helpers/date';

import { isChildAgeValid } from './helpers';
import { DayInputPickerComponent } from '../RequestForm/RequestFormSail';
import { RequestFormSelect } from './RequestFormSelect';
import { getChildAgeErrorMessage } from './helpers';

enum TravelerType {
    child = 'child',
    adult = 'adult',
}
interface IRequestFormTravelers {
    setChildrenNum: typeof setChildrenNum;
    setAdultsNum: typeof setAdultsNum;
    adultsNum: number;
    minAdultAge: number;
    childrenNum: number;
    dateOfDeparture?: number;
    cruiseCompanyCode?: string;
    cruiseNights: number;
    childAges: IAge[];
    isSubmitted?: boolean;
    setChildAge: typeof setChildAge;
}

const MIN_ADULT_AGE_FALLBACK = 18;
const MAX_PASSENGERS = 9;

const travelerIconStyles = {
    fill: '#333333',
};

const RequestFormTravelers = (props: IRequestFormTravelers) => {
    const {
        minAdultAge,
        childAges,
        setChildAge,
        childrenNum,
        adultsNum,
        dateOfDeparture,
        isSubmitted,
        cruiseCompanyCode,
        cruiseNights,
    } = props;
    const adultAgeStart = minAdultAge || MIN_ADULT_AGE_FALLBACK;
    const maxChildAge = adultAgeStart - 1;

    const getPaxOptions = (min: number, max: number) => {
        return [...Array(max + 1).keys()].filter((index) => index >= min);
    };

    const handleSelectChange = (type: TravelerType) => (value) => {
        const handler = {
            [TravelerType.child]: props.setChildrenNum,
            [TravelerType.adult]: props.setAdultsNum,
        }[type];

        const parsedValue = parseInt(value, 10);
        if (parsedValue >= 0) handler(parsedValue);
    };

    return (
        <div className="requestFormContainer__travelers">
            <div className="requestFormContainer__section-label">
                <label>{__('cruiseData.pax.label', 'dreamlines')}</label>
            </div>

            <div className="travelersSelectContainer">
                <div className="travelersSelectContainer__adult">
                    <label htmlFor="adults-num-select">
                        {__('adults', 'dreamlines')}
                        <br />
                        <span>{`(${adultAgeStart}+ ${__(
                            `cruiseData.pax.years`,
                            'dreamlines',
                        )})`}</span>
                    </label>
                    <RequestFormSelect
                        id="adults-num-select"
                        defaultValue={adultsNum}
                        options={getPaxOptions(1, MAX_PASSENGERS)}
                        onChange={handleSelectChange(TravelerType.adult)}
                        icon={<AdultsIcon width={21} height={21} style={travelerIconStyles} />}
                    />
                </div>

                <div className="travelersSelectContainer__child">
                    <label htmlFor="child-num-select">
                        {__('children', 'dreamlines')}
                        <br />
                        <span>{`(0-${maxChildAge} ${__(
                            `cruiseData.pax.years`,
                            'dreamlines',
                        )})`}</span>
                    </label>
                    <RequestFormSelect
                        id="child-num-select"
                        defaultValue={childrenNum}
                        options={getPaxOptions(0, MAX_PASSENGERS)}
                        onChange={handleSelectChange(TravelerType.child)}
                        icon={<ChildrenIcon width={21} height={21} style={travelerIconStyles} />}
                    />
                </div>
            </div>

            {childrenNum > 0 && (
                <RequestFormKidsAge
                    childAges={childAges}
                    cruiseCompanyCode={cruiseCompanyCode}
                    cruiseNights={cruiseNights}
                    isSubmitted={isSubmitted}
                    minAdultAge={maxChildAge}
                    dateOfDeparture={dateOfDeparture}
                    onChange={setChildAge}
                />
            )}
        </div>
    );
};
interface IRequestFormKidsAge {
    childAges: IAge[];
    minAdultAge: number;
    dateOfDeparture: number;
    cruiseCompanyCode: string;
    cruiseNights: number;
    isSubmitted: boolean;
    onChange: typeof setChildAge;
}

const ErrorMessage = ({ childAge, isSubmitted, cruiseCompanyCode, cruiseNights }) => {
    const error = getChildAgeErrorMessage(childAge, cruiseCompanyCode, cruiseNights, isSubmitted);
    if (!error) return null;
    return <div className="kidsAgePickerWrapper__errorMessage">{error}</div>;
};

const RequestFormKidsAge = (props: IRequestFormKidsAge) => {
    const {
        childAges,
        minAdultAge,
        onChange,
        dateOfDeparture,
        isSubmitted,
        cruiseCompanyCode,
        cruiseNights,
    } = props;
    const elementRef = useRef<HTMLDivElement | null>(null);

    const handleChildAgeChange = (index: number, date: Date) => {
        if (!isDateValid(date)) {
            onChange(null, null, index, false);
            return;
        }
        onChange(
            differenceInYears(dateOfDeparture * 1000, date),
            date,
            index,
            isChildAgeValid(dateOfDeparture, date, cruiseNights, cruiseCompanyCode),
        );
    };

    return (
        <div className="kidsAgePickerWrapper" ref={elementRef}>
            <p>{__(`personalData.childrenAge.birthday`, 'dreamlines')}:</p>
            {childAges.map((childAge, index) => (
                <React.Fragment key={`kidAge-${index}`}>
                    <div
                        className="kidsAgePickerWrapper__calendarRow"
                        data-testid={`request-form-children-age-${index}`}
                    >
                        <div className="dayInputPickerWrapper">
                            <DayInputPickerComponent
                                index={index}
                                elementRef={elementRef}
                                error={
                                    !!getChildAgeErrorMessage(
                                        childAge,
                                        cruiseCompanyCode,
                                        cruiseNights,
                                        isSubmitted,
                                    )
                                }
                                childAge={childAge}
                                minAdultAge={minAdultAge}
                                onChange={handleChildAgeChange}
                                departureDate={dateOfDeparture}
                            />
                            <CalendarIcon width={18} height={18} />
                        </div>

                        <div>
                            {index + 1}. {__('general.child', 'dreamlines')}
                        </div>
                    </div>
                    <ErrorMessage
                        cruiseNights={cruiseNights}
                        cruiseCompanyCode={cruiseCompanyCode}
                        childAge={childAge}
                        isSubmitted={isSubmitted}
                    />
                </React.Fragment>
            ))}
        </div>
    );
};

export default RequestFormTravelers;
